// Таблица (components/DataTable/README.md; README «Плотные таблицы»; ux.md, раздел 6) на TanStack
// Table и Virtual. Панель: поиск → «Фильтр» → «Сортировка» → «Колонки» → распорка → одна
// кнопка-акцент. Под панелью — своя строка чипов-условий со «Сбросить всё» справа: «Колонки»
// и кнопка-акцент остаются в строке поиска. Шапка и первая колонка закреплены, широкая таблица
// прокручивается внутри карточки, страница вбок не прокручивается (FR-039б). До 640 px —
// карточки записей. Больше 50 строк — виртуальная прокрутка. Сортировку, поиск и курсор
// выполняет сервер.
//
// Шапка закреплена при прокрутке страницы: пока таблица помещается в карточку, своей области
// прокрутки у неё нет, и шапка встаёт под шапкой листа. Таблица шире карточки (или виртуальная
// прокрутка, или maxHeight) — своя область прокрутки высотой до окна: шапка закреплена внутри
// неё, первая колонка — при прокрутке вбок.
//
// Действия строки (rowMenu): последняя колонка «…» с меню видна при наведении и фокусе (hov);
// правый клик по строке, Shift+F10 и клавиша меню открывают то же меню у «…». После закрытия
// фокус возвращается туда, откуда меню открыли.
//
// 006 (T028; ux.md, раздел 6 «Строка»): выбор строк флажками — колонка флажков перед первой
// (обе закреплены слева), в шапке — «Выбрать все видимые», Shift — диапазон от прошлого флажка.
// Выбор хранит страница (selection, onSelectionChange): таблицы групп делят один выбор и одну
// BulkBar. Выбранная строка — color-accent-soft, как открытая.
import {
  columnVisibilityFeature,
  createColumnHelper,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import type { RowData } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, Columns3, Ellipsis } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  RefObject,
  UIEvent,
} from 'react';
import { cx } from '../lib/cx.ts';
import { useTableDensity } from '../lib/density.tsx';
import type { TableDensity } from '../lib/density.tsx';
import { Icon } from '../lib/icon.tsx';
import { useIsPhone } from '../lib/media.ts';
import { focusMovedOn } from '../lib/return-focus.ts';
import { Button } from './Button.tsx';
import { Dropdown, DropdownCheckboxItem, DropdownLabel } from './Dropdown.tsx';
import { IconButton } from './IconButton.tsx';
import { Skeleton, SkeletonBar } from './Skeleton.tsx';
import { Truncate } from './Truncate.tsx';

/** С этого числа строк — виртуальная прокрутка (README, «Компоненты»). */
export const VIRTUAL_ROWS_FROM = 51;

/** Высота своей области прокрутки по умолчанию: таблица с панелью помещается в окно. */
const SCROLL_MAX_HEIGHT = 'max(320px, calc(100dvh - 240px))';

const features = tableFeatures({ columnVisibilityFeature });

export interface DataTableColumn<T> {
  id: string;
  /** Подпись в шапке, в «Колонках» и в карточке до 640 px. */
  header: string;
  /** Текстовое значение: длинное сокращается многоточием, полностью — в подсказке. */
  value?: (row: T) => string | number | null | undefined;
  /** Своя ячейка: бейдж, ссылка, действия строки. */
  cell?: (row: T) => ReactNode;
  /** Числа — по правому краю, tabular-nums. */
  numeric?: boolean;
  align?: 'start' | 'center' | 'end';
  width?: CSSProperties['width'];
  /** Клик по заголовку сортирует, повторный — меняет направление. */
  sortable?: boolean;
  /** Можно скрыть в «Колонках»; первую колонку скрыть нельзя. */
  hideable?: boolean;
  /** Показывать в карточке до 640 px (по умолчанию — да). */
  inCard?: boolean;
}

export interface DataTableSort {
  id: string;
  desc: boolean;
}

export interface DataTableProps<T extends RowData> {
  /** Имя таблицы для программ чтения экрана: «Пользователи». */
  label: string;
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  getRowId: (row: T) => string;
  /** Левая часть панели: поиск → «Фильтр» → «Сортировка» → «Группировка». */
  toolbar?: ReactNode;
  /** После распорки: одна кнопка-акцент «Создать…». */
  toolbarEnd?: ReactNode;
  /**
   * Строка чипов-условий под панелью (ux.md, раздел 6): «Роль — контролёр», «и», «Состояние —
   * архив». Нет условий — нет и строки.
   */
  chips?: ReactNode;
  /** Справа в строке чипов: «Сбросить всё». */
  chipsEnd?: ReactNode;
  /** Кнопка «Колонки» в панели. */
  columnsMenu?: boolean;
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (visibility: Record<string, boolean>) => void;
  sort?: DataTableSort | null;
  onSortChange?: (sort: DataTableSort) => void;
  /** Клик и Enter по строке открывают карточку записи. */
  onRowClick?: (row: T) => void;
  /**
   * Действия строки — пункты меню «…»: DropdownItem, DropdownSeparator (опасный пункт —
   * последним, за разделителем). Колонка «…» видна при наведении и фокусе; правый клик по
   * строке, Shift+F10 и клавиша меню открывают то же меню. null — у строки действий нет.
   */
  rowMenu?: (row: T) => ReactNode;
  /** Подпись «…» и меню для программ чтения; по умолчанию «Действия: {первая колонка}». */
  rowMenuLabel?: (row: T) => string;
  /**
   * Сюда таблица записывает, откуда открыли меню строки: саму строку (правый клик, Shift+F10)
   * или «…». Передайте тот же ref в returnFocusRef окна, которое открывает пункт меню: после окна
   * фокус вернётся туда же (README «Доступность»).
   */
  menuReturnFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Кнопки «…» строк по id строки: когда строка скрывается вместе со своей «…» (удалили запись),
   * страница переводит фокус на «…» соседней строки, а не роняет его на страницу.
   */
  rowMenuRef?: (rowId: string, element: HTMLButtonElement | null) => void;
  selectedRowId?: string | null;
  /**
   * Выбранные флажками строки по id (ux.md, раздел 6). Есть onSelectionChange — есть и колонка
   * флажков; выбор целиком у страницы: таблицы групп делят один выбор.
   */
  selection?: ReadonlySet<string>;
  onSelectionChange?: (next: ReadonlySet<string>) => void;
  /** Подпись флажка строки для программ чтения: «Выбрать: Морозов О. С.». */
  selectLabel?: (row: T) => string;
  /**
   * list — строка size-row-list, dense — size-row. Не задана — по настройке вида на устройстве
   * (TableDensityProvider).
   */
  density?: TableDensity;
  /** Первая загрузка: скелет строк, только если ожидание дольше 0,3 с. */
  loading?: boolean;
  /** Пустое состояние: «ещё нет» или «ничего не найдено». */
  empty?: ReactNode;
  /** Ошибка загрузки вместо строк. */
  error?: ReactNode;
  /** Следующая страница по курсору. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Своя карточка записи до 640 px; по умолчанию — первая колонка заголовком и пары «подпись — значение». */
  renderCard?: (row: T) => ReactNode;
  /**
   * Своя область прокрутки этой высоты. Без неё таблица прокручивается вместе со страницей,
   * а своя область появляется только у виртуальной прокрутки и у таблицы шире карточки.
   */
  maxHeight?: CSSProperties['maxHeight'];
  /**
   * Виртуальная прокрутка от VIRTUAL_ROWS_FROM строк; false — все строки сразу, без своей
   * области прокрутки (короткий справочник на одну страницу, например матрица прав).
   */
  virtualize?: boolean;
  className?: string;
}

function columnClass(column: DataTableColumn<unknown>): string | undefined {
  if (column.numeric || column.align === 'end') return 'num';
  if (column.align === 'center') return 'center';
  return undefined;
}

/**
 * Shift+F10 и клавиша меню — правый клик с клавиатуры (README «Доступность»). Подсказка «…»
 * называет Shift+F10 (ux.md, раздел 8: клавиша — рядом с действием), список `?` — обе.
 */
const MENU_SHORTCUT = 'Shift+F10';

function isMenuKey(event: KeyboardEvent<HTMLElement>): boolean {
  return event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10');
}

/** Правый клик по ссылке и полю оставляет меню браузера: «Открыть в новой вкладке», «Вставить». */
const NATIVE_MENU = 'a[href], input, textarea, select, [contenteditable="true"]';

/** Выбор пуст: общий объект, чтобы не создавать новый на каждую отрисовку. */
const NO_SELECTION: ReadonlySet<string> = new Set();

/** Shift при нажатии флажка: change флажка приходит из click — у него есть shiftKey. */
function withShift(event: ChangeEvent<HTMLInputElement>): boolean {
  const native = event.nativeEvent;
  return 'shiftKey' in native && native.shiftKey === true;
}

function renderValue<T>(column: DataTableColumn<T>, row: T): ReactNode {
  if (column.cell) return column.cell(row);
  const value = column.value?.(row);
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return <span className="t-num">{value}</span>;
  return <Truncate text={value} />;
}

export function DataTable<T extends RowData>({
  label,
  columns,
  rows,
  getRowId,
  toolbar,
  toolbarEnd,
  chips,
  chipsEnd,
  columnsMenu = false,
  columnVisibility,
  onColumnVisibilityChange,
  sort,
  onSortChange,
  onRowClick,
  rowMenu,
  rowMenuLabel,
  menuReturnFocusRef,
  rowMenuRef,
  selectedRowId,
  selection,
  onSelectionChange,
  selectLabel,
  density: densityProp,
  loading = false,
  empty,
  error,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  renderCard,
  maxHeight,
  virtualize = true,
  className,
}: DataTableProps<T>) {
  const isPhone = useIsPhone();
  const preferred = useTableDensity();
  const density = densityProp ?? preferred;
  const scrollRef = useRef<HTMLDivElement>(null);
  // Таблица шире карточки: своя область прокрутки (вбок и вниз) вместо прокрутки страницы.
  const [wide, setWide] = useState(false);
  // Открытое меню «…» и куда вернуть фокус после него: строка, поле в ней или сама «…».
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const menuReturn = useRef<HTMLElement | null>(null);
  const menuTriggers = useRef(new Map<string, HTMLButtonElement>());
  const [ownVisibility, setOwnVisibility] = useState<Record<string, boolean>>({});
  const visibility = columnVisibility ?? ownVisibility;

  const tableColumns = useMemo(() => {
    const helper = createColumnHelper<typeof features, T>();
    return columns.map((column, index) =>
      helper.display({
        id: column.id,
        header: column.header,
        enableHiding: index > 0 && column.hideable !== false,
      }),
    );
  }, [columns]);

  const byId = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);

  const table = useTable({
    features,
    columns: tableColumns,
    data: rows,
    getRowId,
    state: { columnVisibility: visibility },
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(visibility) : updater;
      if (columnVisibility === undefined) setOwnVisibility(next);
      onColumnVisibilityChange?.(next);
    },
  });

  const visibleColumns = table
    .getVisibleLeafColumns()
    .map((column) => byId.get(column.id))
    .filter((column): column is DataTableColumn<T> => column !== undefined);
  const tableRows = table.getRowModel().rows;

  // Выбор строк флажками: «Выбрать все видимые» — все строки таблицы, Shift — диапазон.
  const selectable = onSelectionChange !== undefined;
  const chosen = selection ?? NO_SELECTION;
  const anchorRef = useRef<string | null>(null);
  const rowIds = tableRows.map((row) => row.id);
  const allChosen = rowIds.length > 0 && rowIds.every((id) => chosen.has(id));
  const someChosen = !allChosen && rowIds.some((id) => chosen.has(id));
  const toggleRow = (rowId: string, checked: boolean, range: boolean) => {
    const next = new Set(chosen);
    const from = range && anchorRef.current !== null ? rowIds.indexOf(anchorRef.current) : -1;
    const to = rowIds.indexOf(rowId);
    const ids =
      from >= 0 && to >= 0 ? rowIds.slice(Math.min(from, to), Math.max(from, to) + 1) : [rowId];
    for (const id of ids) {
      if (checked) next.add(id);
      else next.delete(id);
    }
    anchorRef.current = rowId;
    onSelectionChange?.(next);
  };
  const toggleAll = (checked: boolean) => {
    const next = new Set(chosen);
    for (const id of rowIds) {
      if (checked) next.add(id);
      else next.delete(id);
    }
    anchorRef.current = null;
    onSelectionChange?.(next);
  };
  /** «Выбрать: Морозов О. С.» — по подписи selectLabel или первой колонке. */
  const rowSelectLabel = (row: T): string => {
    if (selectLabel) return selectLabel(row);
    const value = columns[0]?.value?.(row);
    return value === null || value === undefined || value === '' ? 'Выбрать' : `Выбрать: ${value}`;
  };
  const rowCheckbox = (row: T, rowId: string) => (
    <input
      type="checkbox"
      className="t-check"
      aria-label={rowSelectLabel(row)}
      checked={chosen.has(rowId)}
      onChange={(event) => toggleRow(rowId, event.currentTarget.checked, withShift(event))}
    />
  );

  const virtual = virtualize && !isPhone && tableRows.length >= VIRTUAL_ROWS_FROM;
  const rowHeight = density === 'dense' ? 36 : 40;
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
    enabled: virtual,
  });

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasMore || loadingMore || !onLoadMore) return;
    const element = event.currentTarget;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - rowHeight * 5) {
      onLoadMore();
    }
  };

  const toggleSort = (id: string) => {
    if (!onSortChange) return;
    onSortChange(sort?.id === id ? { id, desc: !sort.desc } : { id, desc: false });
  };

  /** Подпись «…»: «Действия: Морозов О. С.». */
  const menuLabel = (row: T): string => {
    if (rowMenuLabel) return rowMenuLabel(row);
    const value = columns[0]?.value?.(row);
    return value === null || value === undefined || value === ''
      ? 'Действия'
      : `Действия: ${value}`;
  };

  /** Меню строки открыто: фокус вернётся туда, откуда открыли, а если туда нельзя — на «…». */
  const rememberReturn = (rowId: string, opener?: HTMLElement) => {
    const target =
      opener && opener.tabIndex >= 0 ? opener : (menuTriggers.current.get(rowId) ?? null);
    menuReturn.current = target;
    if (menuReturnFocusRef) menuReturnFocusRef.current = target;
  };

  /** Правый клик или клавиша меню по строке: меню «…» этой строки. */
  const openRowMenu = (element: HTMLElement, rowId: string) => {
    rememberReturn(rowId, element);
    setMenuRowId(rowId);
  };

  const rowContextMenu = (event: MouseEvent<HTMLElement>, rowId: string) => {
    const target = event.target;
    // Событие из меню (портал) или по ссылке и полю — не наше.
    if (!(target instanceof Element) || !event.currentTarget.contains(target)) return;
    if (target.closest(NATIVE_MENU)) return;
    event.preventDefault();
    const focused = document.activeElement;
    openRowMenu(
      focused instanceof HTMLElement && event.currentTarget.contains(focused)
        ? focused
        : event.currentTarget,
      rowId,
    );
  };

  const rowKeyDown = (event: KeyboardEvent<HTMLElement>, row: T, rowId: string, menu: boolean) => {
    if (menu && isMenuKey(event)) {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !event.currentTarget.contains(target)) return;
      event.preventDefault();
      openRowMenu(target, rowId);
      return;
    }
    if (!onRowClick || event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick(row);
    }
  };

  /** Меню закрылось: фокус — туда, откуда его открыли, если страница не перевела его сама. */
  const menuClosed = (event: Event) => {
    event.preventDefault();
    const target = menuReturn.current;
    if (focusMovedOn() || !target?.isConnected) return;
    target.focus();
  };

  const renderMenu = (row: T, rowId: string, content: ReactNode, inTable: boolean) => {
    const label = menuLabel(row);
    return (
      <Dropdown
        label={label}
        align="end"
        open={menuRowId === rowId}
        onOpenChange={(open) => {
          // Radix открывает меню только с самой «…» — фокус после меню вернётся на неё.
          if (open) rememberReturn(rowId);
          setMenuRowId(open ? rowId : null);
        }}
        onCloseAutoFocus={menuClosed}
        trigger={
          <IconButton
            ref={(element: HTMLButtonElement | null) => {
              if (element) menuTriggers.current.set(rowId, element);
              else menuTriggers.current.delete(rowId);
              rowMenuRef?.(rowId, element);
            }}
            icon={Ellipsis}
            label={label}
            shortcut={MENU_SHORTCUT}
            aria-keyshortcuts="Shift+F10 ContextMenu"
            size="sm"
            className={cx('kp-row-menu', inTable && 'hov')}
          />
        }
      >
        {content}
      </Dropdown>
    );
  };

  /** Нажатия в меню (портал) не доходят до строки: клик не открывает карточку записи. */
  const stopFromMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const hideable = table.getAllLeafColumns().filter((column) => column.getCanHide());
  const showToolbar = toolbar != null || toolbarEnd != null || (columnsMenu && hideable.length > 0);

  const toolbarNode = showToolbar ? (
    <div className="t-toolbar">
      {toolbar}
      {columnsMenu && hideable.length > 0 ? (
        <Dropdown
          label="Колонки"
          trigger={
            <Button variant="ghost" size="sm" icon={Columns3} className="kp-hide-phone">
              Колонки
            </Button>
          }
        >
          <DropdownLabel>Колонки</DropdownLabel>
          {hideable.map((column) => (
            <DropdownCheckboxItem
              key={column.id}
              checked={column.getIsVisible()}
              onCheckedChange={(checked) => column.toggleVisibility(checked)}
            >
              {byId.get(column.id)?.header ?? column.id}
            </DropdownCheckboxItem>
          ))}
        </Dropdown>
      ) : null}
      <span className="sp" />
      {toolbarEnd}
    </div>
  ) : null;

  const chipsNode =
    chips != null ? (
      <div className="kp-table-chips">
        {chips}
        <span className="sp" />
        {chipsEnd}
      </div>
    ) : null;

  const footer =
    hasMore && onLoadMore ? (
      <div className="kp-table-foot">
        <Button variant="ghost" size="sm" loading={loadingMore} onClick={onLoadMore}>
          Показать ещё
        </Button>
      </div>
    ) : null;

  const bodyKind =
    error != null
      ? 'error'
      : loading && rows.length === 0
        ? 'loading'
        : rows.length === 0
          ? 'empty'
          : isPhone
            ? 'cards'
            : 'table';

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || bodyKind !== 'table') return undefined;
    // Ширину меряем до отрисовки: широкая таблица сразу получает свою область прокрутки.
    const measure = () => setWide(scroller.scrollWidth > scroller.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    return () => observer.disconnect();
  }, [bodyKind]);

  let body: ReactNode;
  if (bodyKind === 'error') {
    body = <div className="kp-table-empty">{error}</div>;
  } else if (bodyKind === 'loading') {
    body = (
      <Skeleton minHeight={rowHeight * 5} className="kp-table-skeleton">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="t-row kp-sk-row">
            <SkeletonBar width="28%" height={10} />
            <SkeletonBar width="36%" height={10} />
            <SkeletonBar width="14%" height={10} className="kp-sk-end" />
          </div>
        ))}
      </Skeleton>
    );
  } else if (bodyKind === 'empty') {
    body = <div className="kp-table-empty">{empty}</div>;
  } else if (bodyKind === 'cards') {
    const [first, ...rest] = visibleColumns;
    body = (
      <ul className="kp-cards" aria-label={label}>
        {tableRows.map((row) => {
          const original = row.original;
          const selected = selectedRowId === row.id || chosen.has(row.id);
          const menu = rowMenu?.(original);
          const hasMenu = menu != null && menu !== false;
          return (
            <li
              key={row.id}
              className={cx(selected && 'sel', hasMenu && 'kp-card-menu')}
              onContextMenu={hasMenu ? (event) => rowContextMenu(event, row.id) : undefined}
              onKeyDown={hasMenu ? (event) => rowKeyDown(event, original, row.id, true) : undefined}
            >
              {selectable ? (
                <span className="kp-card-select">{rowCheckbox(original, row.id)}</span>
              ) : null}
              {renderCard ? (
                renderCard(original)
              ) : (
                <>
                  {first ? (
                    onRowClick ? (
                      <button
                        type="button"
                        className="kp-card-title"
                        aria-current={selectedRowId === row.id || undefined}
                        onClick={() => onRowClick(original)}
                      >
                        {first.cell ? first.cell(original) : (first.value?.(original) ?? '—')}
                      </button>
                    ) : (
                      <p className="kp-card-title">
                        {first.cell ? first.cell(original) : (first.value?.(original) ?? '—')}
                      </p>
                    )
                  ) : null}
                  <dl className="t-kv">
                    {rest
                      .filter((column) => column.inCard !== false)
                      .map((column) => (
                        <CardPair key={column.id} label={column.header}>
                          {column.cell ? column.cell(original) : (column.value?.(original) ?? '—')}
                        </CardPair>
                      ))}
                  </dl>
                </>
              )}
              {hasMenu ? (
                // На телефоне наведения нет: «…» карточки видна всегда, справа вверху.
                <div className="kp-card-menu-btn" onClick={stopFromMenu}>
                  {renderMenu(original, row.id, menu, false)}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    );
  } else {
    const items = virtual ? virtualizer.getVirtualItems() : [];
    const paddingTop = virtual && items.length > 0 ? (items[0]?.start ?? 0) : 0;
    const paddingBottom =
      virtual && items.length > 0 ? virtualizer.getTotalSize() - (items.at(-1)?.end ?? 0) : 0;
    const rendered = virtual
      ? items.map((item) => tableRows[item.index]).filter((row) => row !== undefined)
      : tableRows;
    // Колонка «…» — последней, если действия строки есть у таблицы.
    const span = visibleColumns.length + (rowMenu ? 1 : 0) + (selectable ? 1 : 0);
    body = (
      <table
        className="t-tbl"
        aria-label={label}
        aria-rowcount={virtual ? rows.length + 1 : undefined}
      >
        <thead>
          <tr>
            {selectable ? (
              <th scope="col" className="sticky kp-select">
                <input
                  ref={(element) => {
                    if (element) element.indeterminate = someChosen;
                  }}
                  type="checkbox"
                  className="t-check"
                  aria-label="Выбрать все видимые"
                  checked={allChosen}
                  onChange={(event) => toggleAll(event.currentTarget.checked)}
                />
              </th>
            ) : null}
            {visibleColumns.map((column, index) => {
              const active = sort?.id === column.id;
              return (
                <th
                  key={column.id}
                  scope="col"
                  className={cx(
                    index === 0 && 'sticky',
                    columnClass(column as DataTableColumn<unknown>),
                  )}
                  style={column.width !== undefined ? { width: column.width } : undefined}
                  aria-sort={
                    column.sortable
                      ? active
                        ? sort?.desc
                          ? 'descending'
                          : 'ascending'
                        : 'none'
                      : undefined
                  }
                >
                  {column.sortable && onSortChange ? (
                    <button
                      type="button"
                      className="kp-th-btn"
                      data-active={active}
                      onClick={() => toggleSort(column.id)}
                    >
                      {column.header}
                      {active ? <Icon icon={sort?.desc ? ArrowDown : ArrowUp} /> : null}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
            {rowMenu ? (
              <th scope="col" className="kp-row-actions">
                <span className="kp-sr-only">Действия</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {paddingTop > 0 ? (
            <tr aria-hidden="true">
              <td colSpan={span} style={{ height: paddingTop, padding: 0, border: 0 }} />
            </tr>
          ) : null}
          {rendered.map((row) => {
            const original = row.original;
            const selected = selectedRowId === row.id || chosen.has(row.id);
            const menu = rowMenu?.(original);
            const hasMenu = menu != null && menu !== false;
            return (
              <tr
                key={row.id}
                className={cx(selected && 'sel', onRowClick && 'kp-row-click')}
                style={density === 'list' ? { height: rowHeight } : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                aria-current={selectedRowId === row.id || undefined}
                onClick={onRowClick ? () => onRowClick(original) : undefined}
                onContextMenu={hasMenu ? (event) => rowContextMenu(event, row.id) : undefined}
                onKeyDown={
                  onRowClick || hasMenu
                    ? (event) => rowKeyDown(event, original, row.id, hasMenu)
                    : undefined
                }
              >
                {selectable ? (
                  // Нажатие флажка не открывает карточку строки.
                  <td className="sticky kp-select" onClick={stopFromMenu}>
                    {rowCheckbox(original, row.id)}
                  </td>
                ) : null}
                {visibleColumns.map((column, index) => (
                  <td
                    key={column.id}
                    className={cx(
                      index === 0 && 'sticky',
                      columnClass(column as DataTableColumn<unknown>),
                    )}
                  >
                    {renderValue(column, original)}
                  </td>
                ))}
                {rowMenu ? (
                  <td className="kp-row-actions" onClick={stopFromMenu}>
                    {hasMenu ? renderMenu(original, row.id, menu, true) : null}
                  </td>
                ) : null}
              </tr>
            );
          })}
          {paddingBottom > 0 ? (
            <tr aria-hidden="true">
              <td colSpan={span} style={{ height: paddingBottom, padding: 0, border: 0 }} />
            </tr>
          ) : null}
        </tbody>
      </table>
    );
  }

  const scrollsX = wide && bodyKind === 'table';
  // Своя область прокрутки: виртуальная прокрутка, заданная высота или таблица шире карточки.
  const scrolls = virtual || maxHeight !== undefined || scrollsX;
  const scrollStyle: CSSProperties | undefined =
    virtual || scrollsX
      ? { maxHeight: maxHeight ?? SCROLL_MAX_HEIGHT }
      : maxHeight !== undefined
        ? { maxHeight }
        : undefined;

  return (
    <div className={cx('t-card kp-table', className)}>
      {toolbarNode}
      {chipsNode}
      <div
        ref={scrollRef}
        className="kp-table-scroll"
        data-scroll={scrolls || undefined}
        style={scrollStyle}
        onScroll={onScroll}
        // Своя область прокрутки доступна с клавиатуры: стрелки прокручивают её вбок и вниз.
        tabIndex={!isPhone && scrolls ? 0 : undefined}
        role={isPhone ? undefined : 'region'}
        aria-label={isPhone ? undefined : label}
      >
        {body}
      </div>
      {footer}
    </div>
  );
}

function CardPair({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
